import { ArrowDownTrayIcon, ChartBarSquareIcon, ChevronDownIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState } from "react";
import { LoaderSpinner } from "../../components/LoaderSpinner";
import { SelectDropdown } from "../../components/SelectDropdown";
import { getEndOfCurrentCalendarYearYmd, getRangeForType } from "../../helpers/profitAndLoss/common";
import { DatePickerInput } from "../../components/DatePickerInput";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ROUTES } from "../../consts/general";
import { CashFlowLoadingKeys, getCashFlowData } from "../../slices/quickbooks/cashFlowSlice";
import ReactTooltip from "react-tooltip";
import axios from "axios";

const CashFlow = () => {

    const navigate = useNavigate();
    const pageParams = window?.location?.search ? new URLSearchParams(window?.location?.search) : null;
    const dispatch = useDispatch();

    const [isExporting, setIsExporting] = useState(false);
    const [type, setType] = useState(pageParams?.get('type') || "last-month");
    const [startDate, setStartDate] = useState(pageParams?.get('startDate') || "");
    const [endDate, setEndDate] = useState(pageParams?.get('endDate') || "");
    const [valuesType, setValuesType] = useState(pageParams?.get('valuesType') || "months");

    const { cashFlowData, loadingKeys: cashFlowLoadingKeys } = useSelector((state) => state.cashFlow || {});

    const cashIsLoading = cashFlowLoadingKeys.includes(CashFlowLoadingKeys.getCashFlowData);

    const [expanded, setExpanded] = useState({});

    useEffect(() => {
      ReactTooltip.rebuild();
    }, [cashFlowData]);

    const displayColumns = useMemo(() => {
      const cols = Array.isArray(cashFlowData?.columns) ? cashFlowData.columns : [];
      const withoutTotal = cols.filter((c) => c?.key !== "TOTAL");
      return [...withoutTotal, { key: "TOTAL", label: "TOTAL" }];
    }, [cashFlowData?.columns]);

    const topParentColorById = useMemo(() => {
      const out = {};
      const items = Array.isArray(cashFlowData?.data) ? cashFlowData.data : [];
      const topIds = items
        .map((it) => it?.parent?.id)
        .filter((id) => typeof id === "string" && /^ROOT\/S:[^/]+$/.test(id));

      const palette = [
        { bg: "bg-blue-50", text: "text-blue-700" },
        { bg: "bg-red-50", text: "text-red-500" },
        { bg: "bg-amber-50", text: "text-amber-700" },
      ];

      topIds.forEach((id, idx) => {
        out[id] = palette[idx] || palette[palette.length - 1];
      });

      return out;
    }, [cashFlowData?.data]);

    const MIN_ALLOWED_DATE = "2024-01-01";
    const clampToMinAllowed = (ymd) => {
      if (!ymd) return ymd;
      return ymd < MIN_ALLOWED_DATE ? MIN_ALLOWED_DATE : ymd;
    };

    const maxCalendarYearEndYmd = getEndOfCurrentCalendarYearYmd();
    const cashFlowStartPickerMax = endDate
      ? (endDate < maxCalendarYearEndYmd ? endDate : maxCalendarYearEndYmd)
      : maxCalendarYearEndYmd;

    const hasUrlDateParams = useMemo(() => {
        const hasType = !!pageParams?.get("type");
        const hasStart = !!pageParams?.get("startDate");
        const hasEnd = !!pageParams?.get("endDate");
        return hasType || hasStart || hasEnd;
    }, [pageParams]);

    useEffect(() => {
        if (hasUrlDateParams) return;
        if (type !== "last-month") return;
        if (startDate || endDate) return;
        const range = getRangeForType("last-month");
        if (range) {
          setStartDate(range.start);
          setEndDate(range.end);
        }
    }, []);

    useEffect(() => {
        if (!startDate || !endDate) return;
        const params = new URLSearchParams({
          type,
          startDate,
          endDate,
          valuesType,
        });
        dispatch(getCashFlowData({
            startDate,
            endDate,
            valuesType,
        }));
        navigate(`${ROUTES.CASH_FLOW}?${params}`, { replace: true });
    }, [
        type,
        startDate,
        endDate,
        valuesType,
    ]);

    const applyType = (nextType) => {
        setType(nextType);
        if (nextType === "custom-dates") return;
        const range = getRangeForType(nextType);
        if (range) {
          setStartDate(range.start);
          setEndDate(range.end);
        }
    };

    const handleStartDateChange = (nextStart) => {
        const clamped = clampToMinAllowed(nextStart);
        setStartDate(clamped);
        if (endDate && nextStart && nextStart > endDate) {
          setEndDate(clamped);
        }
    };
  
    const handleEndDateChange = (nextEnd) => {
        const clamped = clampToMinAllowed(nextEnd);
        setEndDate(clamped);
        if (startDate && nextEnd && nextEnd < startDate) {
          setStartDate(clamped);
        }
    };

    const handleExportAll = async () => {
        setIsExporting(true);
        try {
            const response = await axios.get(`/api/v1/cash-flow/export`, {
              params: {
                startDate,
                endDate,
                valuesType,
              },
              responseType: "blob",
            });
            const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `cash_flow_${valuesType}_${startDate}_to_${endDate}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Failed to export:", error);
        } finally {
            setIsExporting(false);
        }
    }

    const formatMoney = (n) => {
      const v = Number.isFinite(n) ? n : 0;
      const abs = Math.abs(v);
      const s = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(abs);
      return v < 0 ? `($${s})` : `$${s}`;
    };

    const getValueTextClass = (value, accentTextClass) => {
      const v = Number.isFinite(value) ? value : 0;
      if (v < 0) return "text-red-700";
      return accentTextClass || "text-gray-900";
    };

    const toggleExpanded = (id) => {
      setExpanded((prev) => ({ ...prev, [id]: !prev?.[id] }));
    };

    const getRowTotal = (node, columns) => {
      const cols = Array.isArray(columns) ? columns : [];
      const keys = cols.map((c) => c?.key).filter(Boolean);
      const nonTotal = keys.filter((k) => k !== "TOTAL");
      if (nonTotal.length > 0) {
        return nonTotal.reduce((acc, k) => acc + (Number.isFinite(node?.values?.[k]) ? node.values[k] : 0), 0);
      }
      return Number.isFinite(node?.values?.TOTAL) ? node.values.TOTAL : 0;
    };

    const getCellNumber = (node, colKey, columns) => {
      if (colKey === "TOTAL") return getRowTotal(node, columns);
      return Number.isFinite(node?.values?.[colKey]) ? node.values[colKey] : 0;
    };

    const renderNode = (node, level, columns) => {
      const indent = `${level * 16}px`;
      const isSection = node?.type === "Section";
      const hasChildren = Array.isArray(node?.childrens) && node.childrens.length > 0;
      const isOpen = !!expanded?.[node.id];
      const isQuarterView = valuesType === "quarters";
      const isMonthView = valuesType === "months";
      const isTotalOnlyView = valuesType === "total";

      const descColTemplate = isQuarterView
        ? "560px"
        : isTotalOnlyView
          ? "minmax(320px, 1fr)"
          : "minmax(320px, 2fr)";

      const valueColTemplate = isQuarterView ? "minmax(140px, 1fr)" : "140px";
      const totalColTemplate = isQuarterView ? "180px" : "140px";
      const nonTotalColumns = (Array.isArray(columns) ? columns : []).filter((c) => c?.key !== "TOTAL");
      const hasTotal = (Array.isArray(columns) ? columns : []).some((c) => c?.key === "TOTAL");
      // Avoid `repeat(0, ...)` because some browsers treat it as invalid and the grid falls back,
      // making cells appear like "two rows" in total-only mode.
      const middleCols = nonTotalColumns.length > 0 ? `repeat(${nonTotalColumns.length}, ${valueColTemplate})` : "";
      const gridTemplateColumns = `${descColTemplate} ${middleCols} ${hasTotal ? totalColTemplate : ""}`.trim();
      const shouldStickyTotal = valuesType !== "total" && nonTotalColumns.length > 0;
      const shouldStickyLeft = valuesType !== "total";

      const formatTopAccordionTitle = (title) => {
        const s = String(title || "").trim();
        if (!s) return s;
        return s.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
      };

      const isTopParent =
        isSection &&
        level === 0 &&
        hasChildren &&
        typeof node?.id === "string" &&
        /^ROOT\/S:[^/]+$/.test(node.id);

      const topColors = isTopParent ? topParentColorById?.[node.id] : null;

      const isTopLevelNoChildren = level === 0 && !hasChildren;
      const rowBgClass = isTopLevelNoChildren
        ? "bg-emerald-100"
        : topColors?.bg || (isSection ? "bg-gray-50" : "bg-white");
      const accentTextClass = isTopLevelNoChildren ? "text-emerald-800" : (topColors?.text || "");

      // Hover should apply to the FULL row (including sticky left + sticky total),
      // and should transition smoothly for both parents and leaf children rows.
      const hoverRowClass =
        topColors?.bg === "bg-blue-50"
          ? "hover:bg-blue-100"
          : topColors?.bg === "bg-amber-50"
            ? "hover:bg-amber-100"
            : topColors?.bg === "bg-red-50"
              ? "hover:bg-red-100"
              : isTopLevelNoChildren
                ? "hover:bg-emerald-100"
                : "hover:bg-gray-100";

      const hoverCellClass =
        topColors?.bg === "bg-blue-50"
          ? "group-hover:bg-blue-100"
          : topColors?.bg === "bg-amber-50"
            ? "group-hover:bg-amber-100"
            : topColors?.bg === "bg-red-50"
              ? "group-hover:bg-red-100"
              : isTopLevelNoChildren
                ? "group-hover:bg-emerald-100"
                : "group-hover:bg-gray-100";

      const displayTitle =
        isSection && level === 0 && hasChildren
          ? formatTopAccordionTitle(node.title)
          : node.title;

      const tooltipTextByTopGroup = {
        OperatingActivities:
          "Cash generated (or used) by day-to-day business operations—starts with Net Income and adjusts for non-cash items and working capital changes.",
        InvestingActivities:
          "Cash spent on or received from long-term assets—equipment, software, and other investments (not day-to-day expenses).",
        FinancingActivities:
          "Cash from or paid to owners and lenders—debt proceeds/repayments, equity activity, and distributions.",
      };

      const isTopTooltipRow =
        isSection &&
        level === 0 &&
        hasChildren &&
        typeof node?.group === "string" &&
        Object.prototype.hasOwnProperty.call(tooltipTextByTopGroup, node.group);

      const tooltipId = isTopTooltipRow
        ? `${node.group.replace(/[^a-zA-Z0-9_-]/g, "_")}_tooltip`
        : null;

      return (
        <div key={node.id}>
          <div
            className={[
              "grid items-center gap-0 transition-colors duration-200 ease-in-out border-b border-gray-100 group",
              rowBgClass,
              hoverRowClass,
              hasChildren ? "cursor-pointer" : "",
            ].join(" ")}
            style={{ gridTemplateColumns }}
            onClick={hasChildren ? () => toggleExpanded(node.id) : undefined}
            role={hasChildren ? "button" : undefined}
          >
            <div
              className={[
                "py-3 px-3 text-sm text-gray-700 relative transition-colors duration-200",
                shouldStickyLeft ? "sticky left-0 z-20" : "",
                rowBgClass,
                hoverCellClass,
              ].join(" ")}
              style={{ paddingLeft: indent }}
            >
              {/* Sticky "parser" divider (not border-l) */}
              {shouldStickyLeft ? <div className="absolute top-0 right-0 h-full w-px bg-gray-200" /> : null}
              <div className="flex items-center gap-2 px-4 relative">
                {hasChildren ? (
                  <ChevronDownIcon
                    className={[
                      "h-4 w-4 transition-transform duration-200",
                      accentTextClass || "text-gray-500",
                      isOpen ? "rotate-0" : "-rotate-90",
                    ].join(" ")}
                  />
                ) : (
                  <div className="w-2" />
                )}
                <span className={[isSection ? "font-semibold" : "", accentTextClass].join(" ").trim()}>
                  {displayTitle}
                </span>

                {isTopTooltipRow ? (
                  <>
                    <button
                      type="button"
                      className="ml-1"
                      data-tip
                      data-for={tooltipId}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      aria-label={`${displayTitle} info`}
                    >
                      <InformationCircleIcon className={["h-4 w-4", accentTextClass || "text-gray-500"].join(" ")} />
                    </button>
                    <ReactTooltip id={tooltipId} effect="solid" place="top" className="text-xs normal-case max-w-xs">
                      {tooltipTextByTopGroup[node.group]}
                    </ReactTooltip>
                  </>
                ) : null}
              </div>
            </div>

            {nonTotalColumns.map((c) => (
              (() => {
                const value = getCellNumber(node, c.key, columns);
                return (
              <div
                key={`${node.id}:${c.key}`}
                className={[
                  "py-3 px-3 text-sm text-right tabular-nums font-bold",
                  getValueTextClass(value, accentTextClass),
                ].join(" ")}
              >
                {formatMoney(value)}
              </div>
                );
              })()
            ))}

            {hasTotal ? (
              (() => {
                const totalValue = getCellNumber(node, "TOTAL", columns);
                return (
              <div
                key={`${node.id}:TOTAL`}
                className={[
                  "py-3 px-3 text-sm text-right tabular-nums font-bold relative transition-colors duration-200",
                  shouldStickyTotal ? "sticky right-0 z-20" : "",
                  getValueTextClass(totalValue, accentTextClass),
                  rowBgClass,
                  hoverCellClass,
                ].join(" ")}
              >
                {/* Sticky divider before TOTAL (not border-l) */}
                {shouldStickyTotal ? <div className="absolute top-0 left-0 h-full w-px bg-gray-200" /> : null}
                <div className="relative">{formatMoney(totalValue)}</div>
              </div>
                );
              })()
            ) : null}
          </div>

          {hasChildren ? (
            <div
              className={[
                "transition-all duration-200 ease-in-out",
                isOpen ? "max-h-[5000px] opacity-100 overflow-visible" : "max-h-0 opacity-0 overflow-hidden pointer-events-none",
              ].join(" ")}
            >
              {(node.childrens || []).map((child) => renderNode(child, level + 1, columns))}
            </div>
          ) : null}
        </div>
      );
    };

    return (
        <main className="w-full px-8">
            <div className="h-auto py-6 flex flex-row gap-4 justify-between items-center">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                                <ChartBarSquareIcon className="h-6 w-6 text-white"/>
                            </div>
                            <h1 className="text-xl font-semibold text-gray-900">KINGS LOOT</h1>
                        </div>
                        <div className="bg-green-100 text-green-800 border-green-200 px-4 py-0.5 rounded-full font-bold text-sm whitespace-nowrap">
                            Cash Flow Dashboard
                        </div>
                    </div>
                </div>
                {/* <button 
                  onClick={handleExportAll}
                  disabled={isExporting}
                  className="flex bg-primary transition-all duration-200 rounded-lg justify-between gap-2 items-center text-white text-sm rounded-lg outline-none p-2.5 px-3 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isExporting ? (
                        <>
                            <LoaderSpinner width="16" height="16" border="2" parentHeight="h-4" />
                            <span>Exporting...</span>
                        </>
                    ) : (
                        <>
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            <span>Export All</span>
                        </>
                    )}
                </button> */}
            </div>
            <div className="h-auto py-0 pb-0 flex flex-col">
              <div className="flex flex-row items-center gap-3 flex-wrap">
                {(
                  <>
                    <div className="flex flex-row gap-2 items-center">
                      <span>Reporting Period:</span>
                      <div className="w-52">
                        <SelectDropdown
                          options={[
                            { value: "this-year-to-date", label: "This Year to Date" },
                            { value: "last-month", label: "Last Month" },
                            { value: "last-quarter", label: "Last Quarter" },
                            { value: "last-year", label: "Last Year" },
                            { value: "custom-dates", label: "Custom Dates" },
                          ]}
                          value={type}
                          onChange={applyType}
                          dropdownClassname={"!z-50"}
                          selectClassname={"!bg-white !p-2"}
                          disabled={cashIsLoading}
                        />
                      </div>
                    </div>
                    <div className="flex flex-row gap-2 items-center">
                      <div className="w-52">
                        <DatePickerInput
                          value={startDate}
                          onChange={handleStartDateChange}
                          placeholder="Start date"
                          min={"2024-01-01"}
                          max={cashFlowStartPickerMax}
                          disabled={type !== "custom-dates" || cashIsLoading}
                        />
                      </div>
                    </div>
                    <span>to</span>
                    <div className="flex flex-row gap-2 items-center">
                      <div className="w-52">
                        <DatePickerInput
                          value={endDate}
                          onChange={handleEndDateChange}
                          placeholder="End date"
                          min={(startDate && startDate > "2024-01-01") ? startDate : "2024-01-01"}
                          max={maxCalendarYearEndYmd}
                          disabled={type !== "custom-dates" || cashIsLoading}
                        />
                      </div>
                    </div>
                    <span>Values</span>
                    <div className="flex flex-row gap-2 items-center">
                      <div className="w-52">
                        <SelectDropdown
                          options={[
                            { value: "total", label: "Total Only" },
                            { value: "months", label: "By Month"},
                            { value: "quarters", label: "By Quarter"},
                          ]}
                          value={valuesType}
                          onChange={setValuesType}
                          dropdownClassname={"!z-50"}
                          selectClassname={"!bg-white !p-2"}
                          disabled={cashIsLoading}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            <section className="h-auto py-0 pb-0 flex flex-col">
              {cashIsLoading ? (
                <div className="py-8 flex justify-center">
                  <LoaderSpinner width="70" height="70" border="5" colorSpinner="black" />
                </div>
              ) : (
                <div className="mt-6 rounded-lg border border-gray-200 bg-white p-6">
                  <div className="overflow-auto">
                    <div className={valuesType === "months" ? "min-w-max" : "w-full"}>
                      <div
                        className="sticky top-0 z-10 grid bg-white border-b border-gray-200"
                      style={{
                          gridTemplateColumns: (() => {
                            const desc =
                              valuesType === "quarters"
                                ? "560px"
                                : valuesType === "total"
                                  ? "minmax(320px, 1fr)"
                                  : "minmax(320px, 2fr)";

                            const periodCols = displayColumns.filter((c) => c?.key !== "TOTAL");
                            const periodTemplate = valuesType === "quarters" ? "minmax(140px, 1fr)" : "140px";
                            const middle = periodCols.length > 0 ? `repeat(${periodCols.length}, ${periodTemplate})` : "";
                            const total = valuesType === "quarters" ? "180px" : "140px";

                            return `${desc} ${middle} ${total}`.trim();
                          })(),
                      }}
                      >
                        <div
                          className={[
                            "text-left text-xs font-semibold text-gray-600 py-2 px-3 bg-white relative",
                            valuesType !== "total" ? "sticky left-0 z-20" : "",
                          ].join(" ")}
                        >
                          {/* Sticky "parser" divider (not border-l) */}
                          {valuesType !== "total" ? <div className="absolute top-0 right-0 h-full w-px bg-gray-200" /> : null}
                          <div className="relative">DESCRIPTION</div>
                        </div>

                        {displayColumns.filter((c) => c?.key !== "TOTAL").map((c) => (
                          <div
                            key={c.key}
                            className="text-right text-xs font-semibold text-gray-600 py-2 px-3 whitespace-nowrap"
                          >
                            {String(c.label || c.key).toUpperCase()}
                          </div>
                        ))}

                        <div className={[
                          "text-right text-xs font-semibold text-gray-600 py-2 px-3 whitespace-nowrap bg-white relative",
                          valuesType !== "total" ? "sticky right-0 z-20" : "",
                        ].join(" ")}>
                          {/* Sticky divider before TOTAL (not border-l) */}
                          {valuesType !== "total" ? <div className="absolute top-0 left-0 h-full w-px bg-gray-200" /> : null}
                          <div className="relative">TOTAL</div>
                        </div>
                      </div>
                      <div className="space-y-3">
                        {(cashFlowData?.data || []).map((item) => {
                          const parent = item?.parent;
                          const childrens = item?.childrens || [];
                          if (!parent) return null;

                          return (
                            <div key={parent.id}>
                              {renderNode({ ...parent, childrens }, 0, displayColumns)}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </section>
        </main>
    )
}

export default CashFlow;